import { Show, useContext } from 'solid-js';
import { A } from '@solidjs/router';
import { AppContext } from '../../app-context-provider/app-context-provider';
import clsx from 'clsx';
import { UserIcon } from '../icons-library/user';
import { createUserAccount } from './create-user-account';

type UserControlsProps = {
    showLoginRegisterLabel?: boolean;
    usePadding?: boolean;
    /**
     * 'legacy' (default): existing grey-500 avatar + atos-bold initials.
     * 'ghost':  DS My-account pill — 34×34, bg-dark-muted (#213D46), white label-md/medium initials.
     * Opt-in per header variant. Remove once all consumers migrate.
     */
    variant?: 'legacy' | 'ghost';
};

export const UserControls = (props: UserControlsProps) => {
    const {
        userState,
        localize,
        siteInfo,
        userProfileUrls,
    } = useContext(AppContext);


    const accountHref = () => userProfileUrls.account;
    const { loggedIn, loggedOut, getInitials, handleClick, shouldShowLoginAffordance } = createUserAccount();

    const isGhost = () => props.variant === 'ghost';

    const LoginButton = () => {
        if (isGhost()) {
            /* DS log-in affordance. Wide viewport: primary-small pill with localized label.
               Narrow: icon-only ghost-neutral pill with the user icon (SvgIcon fill=currentColor
               so it inherits the ghost button's fg-default). */
            return (
                <Show
                    when={props.showLoginRegisterLabel}
                    fallback={
                        <button
                            class="icon-button-ghost icon-button-ghost-md"
                            tabIndex={0}
                            onClick={handleClick}
                            aria-label={localize('header_login_icon', 'Link to Login / Register')}
                        >
                            <UserIcon width={20 / 11} height={20 / 11} fill="currentColor" />
                        </button>
                    }
                >
                    <button
                        class="btn-base btn-primary btn-size-sm !my-0"
                        tabIndex={0}
                        onClick={handleClick}
                        aria-label={localize('header_login_icon', 'Link to Login / Register')}
                    >
                        {localize('log-in', 'Log in')}
                    </button>
                </Show>
            );
        }

        return (
            <div class="flex justify-center items-center mx-xxs">
                <button
                    class={clsx(
                        'flex items-center justify-center',
                        'cursor-pointer',
                        'border-none',
                        'transition-colors duration-200',
                        props.showLoginRegisterLabel ? [
                            'pl-s',
                            'pr-s',
                            'pt-xxxs',
                            'pb-xxxs',
                            'rounded-large',
                            '!bg-grey-500',
                            'text-white',
                            'font-atos-bold',
                            'text-[1.1rem]',
                            'whitespace-nowrap',
                            'hover:!bg-grey-400'
                        ] : [
                            'bg-transparent',
                            'p-0'
                        ]
                    )}
                    tabIndex={0}
                    onClick={handleClick}
                    aria-label={localize('header_login_icon', 'Link to Login / Register')}
                >
                    <Show when={props.showLoginRegisterLabel} fallback={
                        <UserIcon
                            width={2.5}
                            height={2.5}
                        />
                    }>
                        {localize('log-in', 'LOG IN')}
                    </Show>
                </button>
            </div>
        );
    };

    const UserAvatar = () => {
        const commonProps = () => ({
            href: accountHref(),
            title: userState.user?.email,
            'aria-label': localize('open-user-profile-menu', 'Open user profile menu'),
        });

        if (isGhost()) {
            /* DS `.my account` — primary button at small size, square 34×34 pill, user initials centered.
               Colors + pill radius + transitions come from .btn-base .btn-primary; label-md typography layered on. */
            return (
                <A
                    class="btn-base btn-primary !my-0 size-cvi-avatar-sm label-md font-cvi-medium no-underline flex items-center justify-center"
                    {...commonProps()}
                >
                    <span class="translate-y-[2px]">{getInitials()}</span>
                </A>
            );
        }

        return (
            <div class="header-icon flex justify-center items-center px-5 cursor-pointer transition-all duration-200 ease-in-out max-mobile:px-[0.9rem]">
                <A
                    class={clsx(
                        'flex items-center justify-center',
                        'w-[3.5rem] h-[3.5rem]',
                        'rounded-full',
                        '!bg-grey-500',
                        'text-white',
                        'font-atos-bold font-extrabold',
                        'text-[1.4rem]',
                        'cursor-pointer',
                        'border-none',
                        'no-underline',
                        'transition-colors duration-200',
                        'hover:!bg-grey-400',
                        props.usePadding && 'mx-[1.43rem]'
                    )}
                    {...commonProps()}
                >
                    {getInitials()}
                </A>
            </div>
        );
    };

    return (
        <>
            <Show when={loggedIn() && (siteInfo.siteType === 'default' || siteInfo.siteType === 'atos-care')}>
                <UserAvatar />
            </Show>

            <Show when={loggedOut() && shouldShowLoginAffordance()}>
                <LoginButton />
            </Show>
        </>
    );
};
